import type { ReactNode } from "react";

import Candidate from "@mpa/toolbox/models/Candidate";
import Contact from "@mpa/toolbox/models/Contact";
import { PartyInfo } from "@mpa/toolbox/models/Party";

import Brief from "../../brief/Brief";
import { PromiseToast } from "../../components/async/PromiseToast";
import Button from "../../components/Button/Button";
import WithContextualMenu from "../../components/ContextualMenu/WithContextualMenu";
import { useDialogConfirm } from "../../components/dialog/useDialogConfirm";
import EmptyView from "../../components/EmptyView";
import List from "../../components/List/List";
import ListItem from "../../components/List/ListItem";
import LightPageLayout from "../../components/Page/LightPageLayout";
import PageHeader from "../../components/Page/PageHeader";
import Spacer from "../../components/space/Spacer";
import Text from "../../components/Text";
import { callOutgoingRequest, useOutgoingRequest } from "../../data/tanstack-query";
import { deleteContact, getCandidates, getContacts } from "../../endpoints/outgoing-requests";
import { PartyAttributeType } from "../../enums/PartyAttributeType";
import useSubmit from "../../hooks/useSubmit";
import { OnMouseOver } from "../../layouts/SupportZone/constants";
import GeneralSupport from "../../layouts/SupportZone/GeneralSupport";
import SpecialSupport from "../../layouts/SupportZone/SpecialSupport";
import { candidateRoute, contactRoute, invitationRoute, newContactsRoute } from "./navigation";

export default function ContactsPage() {
    const candidates = useOutgoingRequest(getCandidates());
    const contacts = useOutgoingRequest(getContacts());
    const [onDeleteContact, deletion] = useSubmit(async (id: string) => {
        await callOutgoingRequest(deleteContact(id));
    }, []);
    return (
        <LightPageLayout>
            <GeneralSupport content={<Brief id="contacts" />} id="contacts" />
            <PromiseToast promise={deletion} message="Contact supprimé" />
            <PageHeader title="Les acteurs de mon projet d'adoption" />
            <SpecialSupport content={<Brief id="famille" />} enabled={OnMouseOver}>
                <Section title="La famille">
                    <List
                        items={candidates}
                        keyExtractor={(candidate) => candidate.findId()}
                        children={(candidate) => <CandidateRow candidate={candidate} />}
                        empty={
                            <EmptyView
                                message="Aucun membre de la famille pour le moment"
                                callToAction={
                                    <Button
                                        action={invitationRoute.getLocation()}
                                        label="Ajouter un membre"
                                    />
                                }
                            />
                        }
                        onCreate={invitationRoute.getLocation()}
                    />
                </Section>
            </SpecialSupport>
            <Spacer scale={8} />
            <Section title="Les contacts">
                <List
                    items={contacts}
                    keyExtractor={(contact) => contact.findId()}
                    children={(contact) => (
                        <ContactRow contact={contact} onDelete={onDeleteContact} />
                    )}
                    empty={
                        <EmptyView
                            message="Aucun contact pour le moment"
                            callToAction={
                                <Button
                                    action={newContactsRoute.getLocation()}
                                    label="Ajouter un contact"
                                />
                            }
                        />
                    }
                    onCreate={newContactsRoute.getLocation()}
                />
            </Section>
        </LightPageLayout>
    );
}

function CandidateRow(props: { candidate: Candidate }) {
    const { candidate } = props;
    return (
        <ListItem
            label={partyName(candidate.infos)}
            help={PartyInfo.find(candidate.infos, PartyAttributeType.Email)?.value}
            action={candidateRoute.getLocation({
                pathParams: { user: candidate.findId() },
            })}
        />
    );
}

function ContactRow(props: { contact: Contact; onDelete: (id: string) => void }) {
    const { contact, onDelete } = props;
    const [showDialog, confirmNode] = useDialogConfirm({
        message: "Confirmation de suppression",
        onConfirm: () => onDelete(contact.findId()),
    });
    return (
        <>
            <WithContextualMenu
                label={partyName(contact.infos)}
                items={[
                    {
                        label: "Supprimer",
                        icon: "cross",
                        action: showDialog,
                        dangerous: true,
                    },
                ]}
            >
                <ListItem
                    label={partyName(contact.infos)}
                    help={PartyInfo.find(contact.infos, PartyAttributeType.Email)?.value}
                    action={contactRoute.getLocation({
                        pathParams: { contact: contact.findId() },
                    })}
                />
            </WithContextualMenu>
            {confirmNode}
        </>
    );
}

function Section(props: { title: string; children: ReactNode }) {
    const { title, children } = props;
    return (
        <div>
            <Text variant="heading3">{title}</Text>
            <Spacer scale={3} />
            {children}
        </div>
    );
}

function partyName(infos: PartyInfo[]) {
    const name = [
        PartyInfo.find(infos, PartyAttributeType.Firstname)?.value,
        PartyInfo.find(infos, PartyAttributeType.Lastname)?.value,
    ]
        .filter(Boolean)
        .join(" ");
    return name || "Sans nom";
}
